import { useTranslation } from 'react-i18next'
import { Card, CardBody, Spinner, useTabParam } from '@structural-climbing/ui'
import { ChipTabs } from '../../design/ChipTabs'
import { competitionBoardTabs, type CompetitionBoard } from '@structural-climbing/validation'
import { ScoringCard } from '../standings/ScoringCard'
import { StandingsTable } from '../standings/StandingsTable'
import { ordinal, RankBadge } from '../standings/standingsUi'
import { BoardTabs } from './BoardTabs'
import { boardEntries } from './boards'
import { CompetitionAbout } from './CompetitionAbout'
import { useCompetitionStandings } from './useCompetitionStandings'
import { formatWallClock } from './wallClock'
import type { CompetitionDetailView } from './types'

interface Props {
  competition: CompetitionDetailView
  myUserId: string | undefined
  apiUrl: string
  getAccessTokenSilently: () => Promise<string>
}

/**
 * A finished competition. Everyone gets the final scoreboard; a competitor also
 * gets their own result up top, and someone who sat this one out is told so
 * rather than being shown a Join button that no longer does anything.
 */
export function CompetitionResults({
  competition,
  myUserId,
  apiUrl,
  getAccessTokenSilently,
}: Props) {
  const { t, i18n } = useTranslation()
  // Open on the category this member competed in, when they did.
  const categoryIds = competition.categories.map((c) => c.id)
  const [category, setCategory] = useTabParam(
    'category',
    categoryIds.length ? categoryIds : ['all'],
    competition.my_entry?.category_id ?? categoryIds[0] ?? 'all',
  )

  const { data, loading } = useCompetitionStandings(
    competition.id,
    category,
    apiUrl,
    getAccessTokenSilently,
  )

  // General, bouldering or lead. Only switchable when the competition is
  // combined. Both in the URL, so a refresh opens on the same category and board.
  const boards = competitionBoardTabs(competition.discipline)
  const [board, setBoard] = useTabParam<CompetitionBoard>('board', boards, boards[0])
  const combined = competition.discipline === 'combined'

  // Your result on the board being read: a combined competition places you
  // once per discipline, and once overall on General.
  const myResult =
    (!combined
      ? data?.myEntry
      : board === 'general'
        ? data?.myGeneral
        : data?.myEntries?.find((e) => e.discipline === board)) ?? null

  return (
    <div className="flex flex-col gap-3">
      <Card className="shadow-sm">
        <CardBody className="gap-1 py-4 text-center">
          <p className="text-xs font-semibold uppercase tracking-wide text-foreground/50">
            {t('competitions.state_ended')}
          </p>
          <p className="text-sm text-foreground/60">
            {t('competitions.endedOn', {
              date: formatWallClock(competition.ends_at, i18n.language),
            })}
          </p>
        </CardBody>
      </Card>

      {/* Above your result, because that changes with the board too. */}
      <BoardTabs discipline={competition.discipline} board={board} onChange={setBoard} />

      {competition.joined ? (
        <Card className="border border-primary/25 bg-primary/5 shadow-sm">
          <CardBody className="gap-3 py-5">
            <h2 className="text-center font-bold">{t('competitions.yourResultTitle')}</h2>
            {loading ? (
              <div className="flex justify-center py-2">
                <Spinner />
              </div>
            ) : myResult ? (
              <>
                <div className="flex items-center justify-center gap-2">
                  <RankBadge rank={myResult.rank} />
                  <span className="text-lg font-bold">
                    {t('competitions.finishedPlace', {
                      place: ordinal(myResult.rank, i18n.language),
                    })}
                  </span>
                </div>
                <div className="flex justify-center gap-6 text-center">
                  <div>
                    <p className="text-xl font-bold">
                      {myResult.totalScore.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    </p>
                    <p className="text-xs text-foreground/50">{t('standings.colScore')}</p>
                  </div>
                  <div>
                    <p className="text-xl font-bold">{myResult.routesClimbed}</p>
                    <p className="text-xs text-foreground/50">
                      {t('competitions.routesToppedLabel')}
                    </p>
                  </div>
                  <div>
                    <p className="text-xl font-bold">{myResult.flashesCount}</p>
                    <p className="text-xs text-foreground/50">{t('competitions.flashesLabel')}</p>
                  </div>
                </div>
              </>
            ) : (
              <p className="text-center text-sm text-foreground/60">
                {t('competitions.joinedButNoTops')}
              </p>
            )}
          </CardBody>
        </Card>
      ) : (
        <Card className="shadow-sm">
          <CardBody className="py-4 text-center text-sm text-foreground/60">
            {t('competitions.didNotTakePart')}
          </CardBody>
        </Card>
      )}

      {/* One tab per category the gym defined, rather than a fixed pair. */}
      {competition.categories.length > 1 && (
        <ChipTabs
          aria-label={t('competitions.categoryFilterLabel')}
          value={category}
          onChange={setCategory}
          options={competition.categories.map((c) => ({ key: c.id, label: c.name }))}
        />
      )}

      <Card className="shadow-sm">
        <CardBody>
          <h2 className="mb-2 font-bold">{t('competitions.finalStandings')}</h2>
          {loading ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : (
            <StandingsTable
              entries={boardEntries(data, competition.discipline, board)}
              myUserId={myUserId}
              apiUrl={apiUrl}
              emptyContent={t('competitions.standingsEmpty')}
            />
          )}
        </CardBody>
      </Card>

      <CompetitionAbout description={competition.description} prizes={competition.prizes} />

      <ScoringCard
        discipline={board === 'general' ? undefined : board}
        scoringType={data?.scoringType}
        combined={combined}
        progressLink={null}
      />
    </div>
  )
}
